import {useContext} from 'react';
import {createPortal} from 'react-dom';
import {Rnd} from 'react-rnd';
import {useIntl} from 'react-intl';
import classNames from 'classnames';
import useFormatter from 'src/hooks/user-formatter';
import {ThemeContext} from 'src/apiContexts';
import {convertDistanceTo, convertVolumeTo, nomenclatureDistance, nomenclatureVolume} from 'src/helpers/conversions';
import styles from './devices-modal.module.scss';
import {useReportJobDevices} from './queries';

interface ReportJobDevicesModalProps {
  jobId: number | null;
  open: boolean;
  onClose: () => void;
}

// Mesma biblioteca e as mesmas props do painel de relatório flutuante sobre o mapa
// (views/reports/floating-report.jsx) -- arrasta só pela área marcada como "drag-handle",
// nunca sai da janela do navegador (bounds="window"), redimensiona por qualquer lado/canto
// (padrão da própria biblioteca, ao contrário do resize:both do CSS, que só dá o canto).
const PANEL_Z_INDEX = 1070;

// "-" pra qualquer dado que o dispositivo não tem cadastrado em /manage/device -- não é erro,
// é cadastro incompleto, e a tabela não pode fingir que sabe o que não tem.
const orDash = (value?: string | null) => value || '—';

const ReportJobDevicesModal = ({jobId, open, onClose}: ReportJobDevicesModalProps) => {
  const intl = useIntl();
  const {getAttributePreference} = useFormatter();
  const theme = useContext(ThemeContext);
  const {data: devices, isFetching, isError} = useReportJobDevices(open ? jobId : null);

  if (!open) {
    return null;
  }

  // Mesma regra de unidade do resto da tela: preferência do usuário, ou a do servidor quando ele força a dele.
  const distanceUnit = getAttributePreference('distanceUnit');
  const volumeUnit = getAttributePreference('volumeUnit');

  // Centralizado na janela real do usuário no momento em que abre -- default do Rnd é só a
  // posição inicial, recalculada aqui a cada montagem (o componente não é montado enquanto
  // fechado, então reabrir sempre recentraliza em vez de lembrar a última posição arrastada).
  // O painel vai direto para o body: o Rnd é position:absolute, e dentro do cartão da tela
  // (position:relative) a posição calculada pela janela seria somada ao canto do cartão.
  const width = Math.min(window.innerWidth * 0.9, 1100);
  const height = Math.min(window.innerHeight * 0.85, 560);
  const x = (window.innerWidth - width) / 2;
  const y = (window.innerHeight - height) / 2;

  return createPortal(
    <>
      <div className={styles.overlay} onClick={onClose} />
      <Rnd
        style={{zIndex: PANEL_Z_INDEX}}
        default={{x, y, width, height}}
        minWidth={640}
        minHeight={320}
        bounds='window'
        dragHandleClassName='drag-handle'
      >
        <div className={styles.panel}>
          <div className={classNames(styles.header, 'drag-handle')}>
            <span className={styles.title} style={{color: theme}}>
              {intl.formatMessage({id: 'reportJobDevicesModalTitle'})}
            </span>
            <button
              type='button'
              className={styles.closeButton}
              aria-label={intl.formatMessage({id: 'sharedClose'})}
              onClick={onClose}
            >
              <i className='fas fa-times' />
            </button>
          </div>

          {isFetching ? (
            <div className={styles.emptyState}>{intl.formatMessage({id: 'sharedLoading'})}</div>
          ) : isError ? (
            <div className={styles.emptyState}>{intl.formatMessage({id: 'reportJobDevicesError'})}</div>
          ) : !devices || devices.length === 0 ? (
            <div className={styles.emptyState}>{intl.formatMessage({id: 'reportJobDevicesEmpty'})}</div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th style={{color: theme}}>{intl.formatMessage({id: 'reportJobDevicesImei'})}</th>
                    <th style={{color: theme}}>{intl.formatMessage({id: 'sharedName'})}</th>
                    <th style={{color: theme}}>{intl.formatMessage({id: 'deviceModel'})}</th>
                    <th style={{color: theme}}>{intl.formatMessage({id: 'devicePlate'})}</th>
                    <th style={{color: theme}}>{intl.formatMessage({id: 'sharedCustomer'})}</th>
                    <th style={{color: theme}}>{intl.formatMessage({id: 'reportSpentFuel'})}</th>
                    <th style={{color: theme}}>{intl.formatMessage({id: 'reportJobDevicesTotalDistance'})}</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={index}>
                      <td>{orDash(device.imei)}</td>
                      <td>{orDash(device.name)}</td>
                      <td>{orDash(device.model)}</td>
                      <td>{orDash(device.plate)}</td>
                      <td>{orDash(device.customerName)}</td>
                      <td>
                        {`${Number(convertVolumeTo(device.spentFuel, volumeUnit)).toFixed(2)} ${nomenclatureVolume(volumeUnit)}`}
                      </td>
                      <td>
                        {`${Number(convertDistanceTo(device.distance, distanceUnit)).toFixed(2)} ${nomenclatureDistance(distanceUnit)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Rnd>
    </>,
    document.body,
  );
};
ReportJobDevicesModal.displayName = 'ReportJobDevicesModal';

export default ReportJobDevicesModal;
